import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import type { TFunction } from 'i18next';

/**
 * Returns the localized video title for promotional contexts.
 *
 * @param tPromo - translation function bound to 'promoSubject' namespace
 * @param trackTitle - title of the track
 * @param ownerLabel - title of the video's owner
 * @param titleCount - number of titles (for pluralization/formatting)
 * @returns localized title string
 */
export function getVideoTitle(
  tPromo: TFunction,
  trackTitle: string,
  ownerLabel: string,
  titleCount: number
): string {
  const titleLimit = 24;

  return tPromo('videoTitle', {
    track: trackTitle,
    owner: ownerLabel,
    count: titleCount, // stays numeric for plural rules
    limit: titleCount < titleLimit ? `${titleCount}` : `${titleLimit}+`
  });
}

/**
 * Returns the localized descriptive promo text for a video.
 *
 * @param tPromo - translation function bound to 'promoSubject' namespace
 * @param trackTitle - title of the track
 * @param ownerLabel - title of the video's owner
 * @returns localized promo description string
 */
export function getVideoPromo(
  tPromo: TFunction,
  trackTitle: string,
  ownerLabel: string
): string {
  return tPromo('videoPromo', {
    track: trackTitle,
    owner: ownerLabel,
  });
}

/** List of supported promo locale codes based on src/locales/promo/subject */
export const supportedPromoLangs: string[] = [
  'bs','pl','tl','cs','id','lv','cy','ar','ja','fa','sv','ca','az','et',
  'hi','vi','gl','ta','no','tr','ur','el','ms','sk','sr','de','hu','nl',
  'en','is','da','it','ro','th','ru','ko','lt','zh','mk','es','bg','he',
  'uk','fr','fi','pt','sl','hr'
];

export interface VideoHelmetProps {
  musicVideo: string;
  videoTitle: string;
  videoLingo: string;
  videoPromo: string;
  supportedLangs?: string[];
}

export const VideoHelmet: FC<VideoHelmetProps> = ({
  musicVideo,
  videoTitle,
  videoLingo,
  videoPromo,
  supportedLangs = supportedPromoLangs,
}) => {
  const baseUrl = `https://title.fm/v/${musicVideo}`;
  return (
    <Helmet>
      <title>{videoTitle}</title>
      <meta name="description" content={videoPromo} />
      <meta property="og:title" content={videoTitle} />
      <meta property="og:description" content={videoPromo} />
      {/* Canonical self */}
      <link rel="canonical" href={`${baseUrl}?hl=${videoLingo}`} />
      {/* All alternates */}
      {supportedLangs.map(code => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={`${baseUrl}?hl=${code}`}
        />
      ))}
      {/* Default fallback */}
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
    </Helmet>
  );
};
// React component for injecting owner page metadata via react-helmet-async
export interface OwnerHelmetProps {
  musicOwner: string;
  ownerLabel: string;
  ownerLingo: string;
  actorPromo: string;
  supportedLangs?: string[];
}

export const OwnerHelmet: FC<OwnerHelmetProps> = ({
  musicOwner,
  ownerLabel,
  ownerLingo,
  actorPromo,
  supportedLangs = supportedPromoLangs,
}) => {
  const baseUrl = `https://title.fm/o/${musicOwner}`;
  return (
    <Helmet>
      <title>{ownerLabel}</title>
      <meta name="description" content={actorPromo} />
      <meta property="og:title" content={ownerLabel} />
      <meta property="og:description" content={actorPromo} />
      {/* Canonical self */}
      <link rel="canonical" href={`${baseUrl}?hl=${ownerLingo}`} />
      {/* All alternates */}
      {supportedLangs.map(code => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={`${baseUrl}?hl=${code}`}
        />
      ))}
      {/* Default fallback */}
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
    </Helmet>
  );
};